/**
 * PublisherModesRail — a slim three-up rail directly under the hero.
 *
 * Built to `devourplay-anchor/Drop-In/team-drops/publisher-drop.html`.
 *
 * Deliberately has no section header. The hero is loud; this is quiet, and a
 * second big heading immediately beneath it would compete. It also carries the
 * page past launch day: pre-release it holds beta and pre-load milestones, and
 * after release the same rail holds the season cadence — so the page still
 * means something on day 400 instead of dying with the countdown.
 */

import React from "react";
import {isSafeHref} from "./play/safeHref";
import "./PublisherModesRail.scss";

export interface RailItem {
    /** "Live now", "This week", "Campaign" — the kicker. */
    kicker: string;
    title: string;
    meta?: string;
    href?: string;
    /** Dims the kicker for things that have not started yet. */
    upcoming?: boolean;
}

interface PublisherModesRailProps {
    items: RailItem[];
}

export function PublisherModesRail({items}: PublisherModesRailProps): React.ReactElement | null {
    if (!items.length) return null;

    return (
        <div className="modes-rail">
            {items.map(item => {
                /*
                 * Publisher-authored, stored, then rendered — same guard the
                 * other href-bearing widgets use. An unsafe value degrades the
                 * row to a non-link rather than planting one.
                 */
                const href = isSafeHref(item.href) ? item.href : undefined;
                const Item = href ? "a" : "div";
                return (
                    <Item
                        key={item.title}
                        className="modes-rail__item"
                        {...(href ? {href} : {})}
                    >
                        <span className={`modes-rail__kicker${item.upcoming ? " modes-rail__kicker--dim" : ""}`}>
                            <i aria-hidden="true" /> {item.kicker}
                        </span>
                        <span className="modes-rail__title">{item.title}</span>
                        {item.meta && <span className="modes-rail__meta">{item.meta}</span>}
                    </Item>
                );
            })}
        </div>
    );
}
